import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import { apiClient, type ApiResponse } from '@/lib/api-client';
import { useSystemLookups } from './hooks/useSystemLookups';

interface SmtpSettingsData {
  host: string;
  port: number;
  username: string;
  password: string;
  fromEmail: string;
  fromName: string;
  useTls: boolean;
  isVerified: boolean;
  verifiedAt: string | null;
}

interface TestConnectionResult {
  success: boolean;
  message: string;
}

/**
 * SmtpSettings — per-tenant SMTP configuration with test connection button.
 * Requirements: 15.1 (system settings), 13.2 (SMTP validation), 13.8 (per-tenant SMTP)
 */
export function SmtpSettings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings', 'smtp'],
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<SmtpSettingsData>>('/settings/smtp');
      return response.data.data;
    },
  });

  const [form, setForm] = useState<SmtpSettingsData>({
    host: '',
    port: 587,
    username: '',
    password: '',
    fromEmail: '',
    fromName: '',
    useTls: true,
    isVerified: false,
    verifiedAt: null,
  });

  const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);
  const [testRecipient, setTestRecipient] = useState('');

  const [initialized, setInitialized] = useState(false);
  if (settings && !initialized) {
    const raw = settings as unknown as Record<string, unknown>;
    setForm({
      host: (raw.host || '') as string,
      port: (raw.port || 587) as number,
      username: (raw.username || '') as string,
      password: '',
      fromEmail: (raw.fromEmail || raw.from_email || '') as string,
      fromName: (raw.fromName || raw.from_name || '') as string,
      useTls: (raw.useTls ?? raw.use_tls ?? true) as boolean,
      isVerified: (raw.isVerified ?? raw.is_verified ?? false) as boolean,
      verifiedAt: (raw.verifiedAt || raw.verified_at || null) as string | null,
    });
    setInitialized(true);
  }

  const updateMutation = useMutation({
    mutationFn: async (data: SmtpSettingsData) => {
      const response = await apiClient.put<ApiResponse<SmtpSettingsData>>('/settings/smtp', data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'smtp'] });
      toast({ variant: 'success', title: 'SMTP settings saved successfully' });
    },
  });

  const testConnectionMutation = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<ApiResponse<TestConnectionResult>>(
        '/settings/smtp/test',
        {
          testRecipient: testRecipient || undefined,
        },
      );
      return response.data.data;
    },
    onSuccess: (data) => {
      setTestResult({ success: true, message: data?.message || 'Test email sent successfully' });
      queryClient.invalidateQueries({ queryKey: ['settings', 'smtp'] });
    },
    onError: (error: unknown) => {
      const axiosError = error as { response?: { data?: { error?: { message?: string } } } };
      const message =
        axiosError?.response?.data?.error?.message ||
        'Connection failed. Check your settings and try again.';
      setTestResult({
        success: false,
        message,
      });
    },
  });

  const handleChange = (field: keyof SmtpSettingsData, value: string | number | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value, isVerified: false }));
    setTestResult(null); // Clear test result on change
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateMutation.mutate(form);
  };

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Connection Status */}
        <Card>
          <CardHeader
            title="Email Configuration (SMTP)"
            description="Configure outgoing email settings for notifications and communications."
            action={
              (settings as unknown as Record<string, unknown>)?.is_verified ||
              settings?.isVerified ? (
                <Badge variant="success" size="md" dot>
                  Verified
                </Badge>
              ) : (
                <Badge variant="warning" size="md" dot>
                  Not Verified
                </Badge>
              )
            }
          />
          <CardContent>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Input
                label="SMTP Host"
                value={form.host}
                onChange={(e) => handleChange('host', e.target.value)}
                required
                placeholder="smtp.example.com"
              />
              <Input
                label="Port"
                type="number"
                value={String(form.port)}
                onChange={(e) => handleChange('port', Number(e.target.value))}
                required
                min={1}
                max={65535}
              />
              <Input
                label="Username"
                value={form.username}
                onChange={(e) => handleChange('username', e.target.value)}
                required
                placeholder="your-email@example.com"
              />
              <Input
                label="Password"
                type="password"
                value={form.password}
                onChange={(e) => handleChange('password', e.target.value)}
                placeholder={settings?.host ? '••••••••' : 'Enter password'}
                hint={settings?.host ? 'Leave blank to keep existing password' : undefined}
              />
              <Input
                label="From Email"
                type="email"
                value={form.fromEmail}
                onChange={(e) => handleChange('fromEmail', e.target.value)}
                required
                placeholder="noreply@yourfirm.ca"
              />
              <Input
                label="From Name"
                value={form.fromName}
                onChange={(e) => handleChange('fromName', e.target.value)}
                placeholder="Your Firm Name"
              />
              <Select
                label="Encryption"
                value={form.useTls ? 'tls' : 'none'}
                onChange={(e) => handleChange('useTls', e.target.value === 'tls')}
                options={[
                  { value: 'tls', label: 'TLS / STARTTLS' },
                  { value: 'none', label: 'None (not recommended)' },
                ]}
              />
            </div>
          </CardContent>
        </Card>

        {/* Test Connection */}
        <Card>
          <CardHeader
            title="Test Connection"
            description="Send a test email to verify your SMTP configuration. Enter a recipient email to receive the test."
          />
          <CardContent>
            <div className="mb-4 max-w-md">
              <Input
                label="Test Recipient Email"
                type="email"
                value={testRecipient}
                onChange={(e) => {
                  setTestRecipient(e.target.value);
                  setTestResult(null);
                }}
                placeholder="you@example.com"
                hint="Email address where the test email will be sent"
              />
            </div>
            <div className="flex items-center gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => testConnectionMutation.mutate()}
                loading={testConnectionMutation.isPending}
                disabled={!form.host || !form.port || !form.username || !form.fromEmail}
              >
                Send Test Email
              </Button>
              {testResult && (
                <div className="flex items-center gap-2">
                  {testResult.success ? (
                    <>
                      <svg
                        className="h-5 w-5 text-green-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-sm font-medium text-green-700 dark:text-green-400">
                        {testResult.message}
                      </span>
                    </>
                  ) : (
                    <>
                      <svg
                        className="h-5 w-5 text-red-500"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        aria-hidden="true"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-sm font-medium text-red-700 dark:text-red-400">
                        {testResult.message}
                      </span>
                    </>
                  )}
                </div>
              )}
            </div>
            {settings?.verifiedAt && (
              <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                Last verified: {new Date(settings.verifiedAt).toLocaleString()}
              </p>
            )}
          </CardContent>
          <CardFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => settings && setForm({ ...settings, password: '' })}
            >
              Reset
            </Button>
            <Button type="submit" loading={updateMutation.isPending}>
              Save SMTP Settings
            </Button>
          </CardFooter>
        </Card>
      </form>

      {/* Reply-To Rules */}
      <ReplyToRulesSection />
    </div>
  );
}

// ─── Reply-To Rules Section ──────────────────────────────────────────────────

interface ReplyToRule {
  id: string;
  tenant_id: string;
  contact_category: string | null;
  reply_to_email: string;
  reply_to_name: string | null;
  is_default: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

function ReplyToRulesSection() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data: categories } = useSystemLookups('contact_category');

  const { data: rules, isLoading } = useQuery({
    queryKey: ['settings', 'reply-to-rules'],
    queryFn: async () => {
      const res = await apiClient.get<ApiResponse<ReplyToRule[]>>('/notifications/reply-to-rules');
      return res.data.data ?? [];
    },
  });

  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRule, setEditingRule] = useState<ReplyToRule | null>(null);
  const [formCategory, setFormCategory] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formName, setFormName] = useState('');
  const [formIsDefault, setFormIsDefault] = useState(false);

  const resetForm = () => {
    setFormCategory('');
    setFormEmail('');
    setFormName('');
    setFormIsDefault(false);
    setEditingRule(null);
    setShowAddForm(false);
  };

  const createMutation = useMutation({
    mutationFn: async (data: {
      contact_category: string | null;
      reply_to_email: string;
      reply_to_name: string | null;
      is_default: boolean;
    }) => {
      const res = await apiClient.post<ApiResponse<ReplyToRule>>(
        '/notifications/reply-to-rules',
        data,
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'reply-to-rules'] });
      toast({ variant: 'success', title: 'Reply-to rule created' });
      resetForm();
    },
    onError: () => {
      toast({ variant: 'error', title: 'Failed to create rule' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: {
        contact_category: string | null;
        reply_to_email: string;
        reply_to_name: string | null;
        is_default: boolean;
      };
    }) => {
      const res = await apiClient.put<ApiResponse<ReplyToRule>>(
        `/notifications/reply-to-rules/${id}`,
        data,
      );
      return res.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'reply-to-rules'] });
      toast({ variant: 'success', title: 'Reply-to rule updated' });
      resetForm();
    },
    onError: () => {
      toast({ variant: 'error', title: 'Failed to update rule' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/notifications/reply-to-rules/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings', 'reply-to-rules'] });
      toast({ variant: 'success', title: 'Reply-to rule deleted' });
    },
  });

  const handleSave = () => {
    if (!formEmail.trim()) {
      toast({ variant: 'error', title: 'Reply-to email is required' });
      return;
    }

    const payload = {
      contact_category: formCategory || null,
      reply_to_email: formEmail.trim(),
      reply_to_name: formName.trim() || null,
      is_default: formIsDefault,
    };

    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleEdit = (rule: ReplyToRule) => {
    setEditingRule(rule);
    setFormCategory(rule.contact_category || '');
    setFormEmail(rule.reply_to_email);
    setFormName(rule.reply_to_name || '');
    setFormIsDefault(rule.is_default);
    setShowAddForm(true);
  };

  const getCategoryLabel = (code: string | null) => {
    if (!code) return 'All Categories (Default)';
    return categories?.find((c) => c.code === code)?.label ?? code;
  };

  return (
    <Card>
      <CardHeader
        title="Reply-To Rules"
        description="Configure which reply-to email address to use based on the contact's category. When a client replies to an email, the reply will be directed to the configured address."
        action={
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              resetForm();
              setShowAddForm(!showAddForm);
            }}
          >
            {showAddForm ? 'Cancel' : 'Add Rule'}
          </Button>
        }
      />
      <CardContent>
        {/* Add/Edit Form */}
        {showAddForm && (
          <div className="mb-4 rounded-lg border border-border p-4 dark:border-border-dark">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Select
                label="Contact Category"
                value={formCategory}
                onChange={(e) => setFormCategory(e.target.value)}
                options={(categories ?? [])
                  .filter((c) => c.isActive)
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((c) => ({ value: c.code, label: c.label }))}
                placeholder="All (default fallback)"
              />
              <Input
                label="Reply-To Email"
                type="email"
                value={formEmail}
                onChange={(e) => setFormEmail(e.target.value)}
                required
                placeholder="support@yourfirm.com"
              />
              <Input
                label="Reply-To Name (optional)"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Support Team"
              />
              <div className="flex items-end gap-3 pb-1">
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    checked={formIsDefault}
                    onChange={(e) => setFormIsDefault(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-brand-600"
                  />
                  Default rule (used when no category matches)
                </label>
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button
                type="button"
                size="sm"
                onClick={handleSave}
                loading={createMutation.isPending || updateMutation.isPending}
              >
                {editingRule ? 'Update Rule' : 'Save Rule'}
              </Button>
            </div>
          </div>
        )}

        {/* Rules List */}
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading rules...</p>
        ) : rules && rules.length > 0 ? (
          <div className="divide-y divide-border dark:divide-border-dark">
            {rules.map((rule) => (
              <div key={rule.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {getCategoryLabel(rule.contact_category)}
                    </span>
                    {rule.is_default && (
                      <Badge variant="info" size="sm">
                        Default
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {rule.reply_to_name
                      ? `${rule.reply_to_name} <${rule.reply_to_email}>`
                      : rule.reply_to_email}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(rule)}>
                    Edit
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteMutation.mutate(rule.id)}
                    loading={deleteMutation.isPending}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No reply-to rules configured. The From Email address will be used for all replies.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
